
import React, { useState, useEffect } from 'react';
import { MENU_ITEMS, DEFAULT_TABLES } from './constants';
import { MenuItem, Order, Reservation, OrderSession, Table, AppSettings, CartItem } from './types';
import { PosView } from './components/PosView';
import { ReservationView } from './components/ReservationView';
import { ScheduleView } from './components/ScheduleView';
import { HistoryView } from './components/HistoryView';
import { MenuManager } from './components/MenuManager';
import { SettingsView } from './components/SettingsView';
import { LoginView } from './components/LoginView';
import { Concierge } from './components/Concierge';
import { Clock } from './components/Clock';
import { LayoutGrid, CalendarRange, History, UtensilsCrossed, Settings, Menu, X, CalendarCheck, Loader2, LogOut, AlertCircle, RotateCcw, Database } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from './services/db';

export const getJapanTodayStr = () => {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
  const parts = formatter.formatToParts(now);
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;
  return `${year}-${month}-${day}`;
};

const DEFAULT_SETTINGS: AppSettings = {
  storeName: 'spacesimple',
  storeLogo: '🌿',
  businessStartTime: '10:00',
  businessEndTime: '22:00',
  tableNamePosition: { x: 50, y: 50 },
  tableNameSize: 16,
  tableNameColor: '#44403c',
  showNameBackground: true,
  nameBackgroundColor: '#ffffff',
  reservationLimits: {
    timeWindowMinutes: 120,
    maxTables: 6,
    maxGuests: 15
  },
  closedDays: [],
  closedDates: [],
  openDates: [],
  autoOrderConfig: { enabled: true, menuItemId: 'daily-special' },
  enableRealtimeClock: true,
  announcement: '',
  showFloorStatusHeader: true,
  showCalendarHint: true
};

export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [activeTab, setActiveTab] = useState<'pos' | 'schedule' | 'reservation' | 'history' | 'menu' | 'settings'>('pos');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<string>(getJapanTodayStr());
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [appSettings, setAppSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [menuItems, setMenuItems] = useState<MenuItem[]>(MENU_ITEMS);
  const [tables, setTables] = useState<Table[]>(DEFAULT_TABLES);
  const [activeSessions, setActiveSessions] = useState<OrderSession[]>([]); 
  const [orders, setOrders] = useState<Order[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);

  const loadData = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setLoadError(null);
    try {
      const data = await db.getAllData();
      if (data.settings) {
        setAppSettings(prev => ({ ...prev, ...data.settings }));
      }
      if (data.tables && data.tables.length) setTables(data.tables);
      if (data.menuItems && data.menuItems.length) setMenuItems(data.menuItems);
      setOrders(data.orders || []);
      setReservations(data.reservations || []);
      setIsAuthenticated(true);
    } catch (e: any) {
      console.error('Failed to load data:', e);
      if (e.message === 'AUTH_REQUIRED') {
        setIsAuthenticated(false);
      } else {
        const msg = e.message || '';
        if (msg.includes('DATABASE_NOT_INITIALIZED') || msg.includes('no such table')) {
          setLoadError('データベース未接続 (初期設定が必要)');
        } else if (msg.includes('TIMEOUT')) {
          setLoadError('接続タイムアウト (サーバーを確認してください)');
        } else {
          setLoadError('データの読み込みに失敗しました。');
        }
        if (!silent) setIsAuthenticated(true); 
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('spacesimple_token');
    if (token) {
      loadData();
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setTimeout(() => {
      loadData(true); 
    }, 100);
  };

  const handleLogout = () => {
    localStorage.removeItem('spacesimple_token');
    setIsAuthenticated(false);
    setLoadError(null);
  };

  const handleUpdateSettings = async (newSettings: AppSettings) => {
    setAppSettings(newSettings);
    await db.saveSettings(newSettings);
  };

  const handleUpdateTable = async (updatedTable: Table) => {
    setTables(prev => prev.map(t => t.id === updatedTable.id ? updatedTable : t));
    await db.saveTable(updatedTable);
  };

  const handleReorderTables = async (newTables: Table[]) => {
    setTables(newTables);
    // 全てのテーブルを新しい順序で保存
    for (const table of newTables) {
      await db.saveTable(table);
    }
  };

  const handleAddTable = async (name: string, capacity: number = 2) => {
    const newTable: Table = { 
      id: Date.now().toString(), 
      name, 
      capacity: capacity || 2,
      backgroundColor: '#ffffff',
      textColor: '#44403c'
    };
    setTables(prev => [...prev, newTable]);
    await db.saveTable(newTable);
  };

  const handleDeleteTable = async (id: string) => {
    setTables(prev => prev.filter(t => t.id !== id));
    await db.deleteTable(id);
  };

  const handleUpdateMenuItem = async (item: MenuItem) => {
    setMenuItems(prev => prev.map(old => old.id === item.id ? item : old));
    await db.saveMenuItem(item);
  };

  const handleAddMenuItem = async (item: MenuItem) => {
    setMenuItems(prev => [...prev, item]);
    await db.saveMenuItem(item);
  };

  const handleDeleteMenuItem = async (id: string) => {
    setMenuItems(prev => prev.filter(i => i.id !== id));
    await db.deleteMenuItem(id);
  };

  const handleAddOrder = async (order: Order) => {
    setOrders(prev => [order, ...prev]);
    await db.saveOrder(order);
  };

  const handleUpdateOrder = async (order: Order) => {
    setOrders(prev => prev.map(old => old.id === order.id ? order : old));
    await db.saveOrder(order);
  };

  const handleAddReservation = async (res: Reservation) => {
    setReservations(prev => [...prev, res]);
    await db.saveReservation(res);
  };

  const handleUpdateReservation = async (updatedRes: Reservation) => {
    setReservations(prev => prev.map(r => r.id === updatedRes.id ? updatedRes : r));
    await db.saveReservation(updatedRes);
  };

  const handleUpdateReservationStatus = async (id: string, status: Reservation['status']) => {
    setReservations(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    const target = reservations.find(r => r.id === id);
    if (target) {
      await db.saveReservation({ ...target, status });
    }
  };

  const handleCreateSession = (tableId: string, customerName: string, guestCount: number, notes?: string) => {
    if (activeSessions.find(s => s.tableId === tableId)) return;
    let initialItems: CartItem[] = [];
    let initialTotal = 0;
    if (appSettings.autoOrderConfig?.enabled && appSettings.autoOrderConfig.menuItemId) {
      const autoItem = menuItems.find(i => i.id === appSettings.autoOrderConfig?.menuItemId);
      if (autoItem && autoItem.isActive) {
        initialItems = [{ ...autoItem, quantity: guestCount }];
        initialTotal = autoItem.price * guestCount;
      }
    }
    const newSession: OrderSession = { 
      id: Date.now().toString(), 
      tableId, 
      customerName, 
      startTime: Date.now(), 
      items: initialItems, 
      total: initialTotal, 
      guestCount,
      notes 
    };
    setActiveSessions(prev => [...prev, newSession]);
  };

  const handleJumpToSchedule = (date: string) => {
    setScheduleDate(date);
    setActiveTab('schedule');
  };

  if (isAuthenticated === null) {
    return (
      <div className="h-screen flex items-center justify-center bg-stone-50">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
          <Loader2 className="text-orange-400" size={40} />
        </motion.div>
      </div>
    );
  }

  if (isAuthenticated === false) {
    return <LoginView onSuccess={handleLoginSuccess} />;
  }

  if (loadError) {
    const isDbUnconnected = loadError.includes('データベース');
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-stone-50 p-6 text-center">
        {isDbUnconnected ? <Database className="text-amber-400 mb-4" size={48} /> : <AlertCircle className="text-red-400 mb-4" size={48} />}
        <h2 className="text-xl font-bold text-stone-800 mb-2">{loadError}</h2>
        <p className="text-stone-500 max-w-sm mb-6">
          {isDbUnconnected 
            ? 'サーバーがデータベースにアクセスできませんでした。' 
            : 'サーバーとの通信中に問題が発生しました。しばらく待ってから再試行してください。'}
        </p>
        <div className="flex gap-4">
           <button onClick={() => loadData()} className="flex items-center gap-2 bg-stone-800 text-white px-6 py-3 rounded-2xl font-bold shadow-lg transition-transform active:scale-95">
             <RotateCcw size={18} /> 再試行
           </button>
           <button onClick={handleLogout} className="bg-stone-200 text-stone-600 px-6 py-3 rounded-2xl font-bold transition-transform active:scale-95">
             ログアウト
           </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'pos', label: '店舗', icon: LayoutGrid, color: 'text-orange-600 bg-orange-50' },
    { id: 'schedule', label: '予定', icon: CalendarCheck, color: 'text-indigo-600 bg-indigo-50' },
    { id: 'reservation', label: 'カレンダー', icon: CalendarRange, color: 'text-green-600 bg-green-50' },
    { id: 'history', label: '履歴', icon: History, color: 'text-blue-600 bg-blue-50' },
    { id: 'menu', label: 'メニュー', icon: UtensilsCrossed, color: 'text-rose-600 bg-rose-50' },
    { id: 'settings', label: '設定', icon: Settings, color: 'text-stone-600 bg-stone-50' },
  ];

  return (
    <div className="h-[100dvh] bg-stone-100 text-cafe-text font-sans selection:bg-orange-100 flex flex-col overflow-hidden">
      <AnimatePresence>
        {isLoading && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-2 left-1/2 -translate-x-1/2 z-[100] bg-white/80 backdrop-blur px-3 py-1.5 rounded-full shadow-lg border border-stone-200 flex items-center gap-2"
          >
            <Loader2 className="animate-spin text-orange-400" size={14} />
            <span className="text-[10px] font-bold text-stone-500 uppercase tracking-widest">Syncing...</span>
          </motion.div>
        )}
      </AnimatePresence>

      <header className="bg-white/90 backdrop-blur-md px-4 py-3 border-b border-stone-200 sticky top-0 z-30 shrink-0">
        <div className="flex justify-between items-center max-w-5xl mx-auto w-full relative">
          <div className="flex items-center gap-3 w-1/3 overflow-hidden">
            <span className="text-xl">{appSettings.storeLogo || '🌿'}</span>
            <div className="flex flex-col justify-center min-w-0">
               <h1 className="font-bold tracking-widest text-stone-800 text-xs sm:text-sm truncate leading-tight hidden sm:block">{appSettings.storeName || 'spacesimple'}</h1>
               <span className="font-bold text-stone-700 text-lg sm:text-xs leading-none">
                  <span className="sm:hidden truncate block">
                    {activeTab === 'pos' ? (appSettings.storeName || 'spacesimple') : (tabs.find(t => t.id === activeTab)?.label)}
                  </span>
               </span>
            </div>
          </div>
          <Clock enabled={appSettings.enableRealtimeClock ?? true} />
          <div className="flex justify-end w-1/3 items-center gap-2">
             <div className="hidden md:flex gap-1">
               {tabs.map(tab => (
                 <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`p-2 rounded-lg transition-colors ${activeTab === tab.id ? 'bg-stone-100 text-stone-800' : 'text-stone-400 hover:text-stone-600'}`} title={tab.label}><tab.icon size={20} /></button>
               ))}
               <button onClick={handleLogout} className="p-2 text-stone-400 hover:text-red-500 rounded-lg transition-colors" title="ログアウト"><LogOut size={20} /></button>
             </div>
             <button onClick={() => setIsMenuOpen(true)} className="p-2 rounded-full hover:bg-stone-100 md:hidden"><Menu size={24} className="text-stone-700" /></button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full p-2 md:p-6 overflow-hidden relative">
        <motion.div key={activeTab} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }} className="h-full">
          {activeTab === 'pos' && (
            <PosView 
              tables={tables} 
              appSettings={appSettings} 
              menuItems={menuItems} 
              activeSessions={activeSessions} 
              orders={orders} 
              reservations={reservations} 
              onCreateSession={handleCreateSession} 
              onUpdateSession={(id, items, total) => setActiveSessions(prev => prev.map(s => s.id === id ? { ...s, items, total } : s))} 
              onUpdateSessionInfo={(id, name, guests) => setActiveSessions(prev => prev.map(s => s.id === id ? { ...s, customerName: name, guestCount: guests } : s))} 
              onDeleteSession={(id) => setActiveSessions(prev => prev.filter(s => s.id !== id))} 
              onCheckoutSession={(s) => { 
                const newOrder: Order = { id: s.id, timestamp: Date.now(), items: s.items, total: s.total, label: `${tables.find(t => t.id === s.tableId)?.name || ''} - ${s.customerName}` };
                handleAddOrder(newOrder); 
                setActiveSessions(prev => prev.filter(sess => sess.id !== s.id)); 
              }} 
              onAddReservation={handleAddReservation} 
            />
          )}
          {activeTab === 'schedule' && <ScheduleView viewDate={scheduleDate} reservations={reservations} tables={tables} appSettings={appSettings} onUpdateReservation={handleUpdateReservation} onDateChange={setScheduleDate} />}
          {activeTab === 'reservation' && <ReservationView reservations={reservations} appSettings={appSettings} tables={tables} onAddReservation={handleAddReservation} onUpdateReservation={handleUpdateReservation} onUpdateStatus={handleUpdateReservationStatus} onJumpToSchedule={handleJumpToSchedule} />}
          {activeTab === 'history' && <HistoryView orders={orders} menuItems={menuItems} onDeleteOrder={(id) => handleUpdateOrder(orders.find(o => o.id === id)! && { ...orders.find(o => o.id === id)!, isDeleted: true })} onRestoreOrder={(id) => handleUpdateOrder(orders.find(o => o.id === id)! && { ...orders.find(o => o.id === id)!, isDeleted: false })} onUpdateOrder={handleUpdateOrder} />}
          {activeTab === 'menu' && <MenuManager items={menuItems} onUpdateItem={handleUpdateMenuItem} onAddItem={handleAddMenuItem} onDeleteItem={handleDeleteMenuItem} />}
          {activeTab === 'settings' && <SettingsView tables={tables} menuItems={menuItems} appSettings={appSettings} onUpdateSettings={handleUpdateSettings} onUpdateTable={handleUpdateTable} onReorderTables={handleReorderTables} onAddTable={handleAddTable} onDeleteTable={handleDeleteTable} />}
        </motion.div>
      </main>

      {/* AI Concierge Component */}
      <Concierge 
        orders={orders} 
        menu={menuItems} 
        reservations={reservations} 
        settings={appSettings} 
      />

      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsMenuOpen(false)} className="fixed inset-0 bg-black/20 z-40 md:hidden" />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} className="fixed top-0 right-0 h-full w-64 bg-white shadow-2xl z-50 p-6 flex flex-col md:hidden">
              <div className="flex justify-between items-center mb-8"><span className="font-bold text-lg">Menu</span><button onClick={() => setIsMenuOpen(false)} className="p-2 bg-stone-100 rounded-full"><X size={20}/></button></div>
              <div className="space-y-2 flex-1">
                {tabs.map(tab => (<button key={tab.id} onClick={() => { setActiveTab(tab.id as any); setIsMenuOpen(false); }} className={`w-full flex items-center gap-3 p-3 rounded-xl font-bold transition-colors ${activeTab === tab.id ? 'bg-stone-100 text-stone-800' : 'text-stone-500 hover:bg-stone-50'}`}><tab.icon size={20} />{tab.label}</button>))}
              </div>
              <button onClick={handleLogout} className="w-full flex items-center gap-3 p-3 rounded-xl font-bold text-red-400 hover:bg-red-50 transition-colors mt-auto">
                <LogOut size={20} /> ログアウト
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
